import { Element, useNode } from '@craftjs/core';
import { Box, SxProps, Theme } from '@mui/material';
import * as React from 'react';

import { useIsBlockEditorEditMode } from '../BlockEditor.context';
import { ALLOWED_TYPES, BLOCK_DISPLAY_NAMES } from '../blockEditor.types';
import { BlockEditorCanvasContainer } from '../BlockEditorCanvasContainer/BlockEditorCanvasContainer';

export interface ListItemsSectionProps {
  sx?: SxProps<Theme>;
}

/**
 * A layout container for list items in the block editor.
 *
 * Like TwoColumnLayout, this component creates a canvas child via `<Element>`
 * where content blocks can be dropped. This pattern ensures consistent
 * architecture across all layout components.
 *
 * Structure:
 * - ListItemsSection (layout wrapper, not a canvas)
 *   └── default (BlockEditorCanvasContainer, IS a canvas, holds children)
 */
export function ListItemsSection({ sx }: ListItemsSectionProps) {
  const {
    connectors: { connect },
  } = useNode();
  const isEditMode = useIsBlockEditorEditMode();

  // Element props to pass to BlockEditorCanvasContainer
  // Using type assertion because Element's types don't include child component props
  const elementProps = {
    id: 'default',
    is: BlockEditorCanvasContainer,
    canvas: true,
    sx,
    custom: {
      allowedTypes: ALLOWED_TYPES.containerContent,
    },
  } as const;

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref && isEditMode) connect(ref);
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
    >
      <Element {...(elementProps as React.ComponentProps<typeof Element>)} />
    </Box>
  );
}

ListItemsSection.craft = {
  displayName: BLOCK_DISPLAY_NAMES.ListItemsSection,
  props: {},
};
